import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from "lucide-react";

/**
 * Lightbox galería para páginas de ejemplo de un informe (diagnóstico / consultoría).
 * Navegación con flechas, puntos, teclado (Esc/←/→) y cierre al hacer clic fuera.
 */
export default function ReportLightbox({ pages, kicker, index, onClose, onPrev, onNext, onSelect, testIdPrefix }) {
  const [zoom, setZoom] = useState(false);
  const containerRef = useRef(null);
  const dragState = useRef({ dragging: false, startY: 0, startScroll: 0, moved: false });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  const current = pages[index];

  useEffect(() => {
    setZoom(false);
  }, [index]);

  const handlePointerDown = (e) => {
    if (!zoom) return;
    dragState.current = {
      dragging: true,
      startY: e.clientY,
      startX: e.clientX,
      startScroll: containerRef.current.scrollTop,
      startScrollX: containerRef.current.scrollLeft,
      moved: false,
    };
  };

  const handlePointerMove = (e) => {
    const s = dragState.current;
    if (!s.dragging) return;
    const dy = e.clientY - s.startY;
    const dx = e.clientX - s.startX;
    if (Math.abs(dy) > 4 || Math.abs(dx) > 4) s.moved = true;
    if (s.moved) {
      containerRef.current.scrollTop = s.startScroll - dy;
      containerRef.current.scrollLeft = s.startScrollX - dx;
    }
  };

  const handlePointerUp = () => {
    const wasDragging = dragState.current.moved;
    dragState.current.dragging = false;
    if (zoom && !wasDragging) setZoom(false);
  };

  const handlePointerLeave = () => {
    dragState.current.dragging = false;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      data-testid={`${testIdPrefix}-lightbox`}
      className="fixed inset-0 z-[70] bg-[#04120E]/90 backdrop-blur-sm flex items-center justify-center p-4 lg:p-8"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-h-[94vh] flex flex-col ${zoom ? "max-w-6xl" : "max-w-3xl"}`}
      >
        <div className="flex items-center justify-between gap-4 mb-4 text-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A84C]">
              {kicker}
            </div>
            <div className="font-display text-lg md:text-xl truncate" data-testid={`${testIdPrefix}-lightbox-title`}>{current.label}</div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-xs text-white/60 tabular-nums" data-testid={`${testIdPrefix}-lightbox-counter`}>
              {index + 1} / {pages.length}
            </span>
            <button
              onClick={() => setZoom(!zoom)}
              aria-label={zoom ? "Reducir" : "Ampliar"}
              data-testid={`${testIdPrefix}-lightbox-zoom`}
              className="w-11 h-11 rounded-full border border-white/25 grid place-items-center text-white hover:bg-[#C9A84C] hover:text-[#06231D] hover:border-[#C9A84C] transition-colors"
            >
              {zoom ? <Minimize2 size={18} strokeWidth={1.6} /> : <Maximize2 size={18} strokeWidth={1.6} />}
            </button>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              data-testid={`${testIdPrefix}-lightbox-close`}
              className="w-11 h-11 rounded-full border border-white/25 grid place-items-center text-white hover:bg-[#C9A84C] hover:text-[#06231D] hover:border-[#C9A84C] transition-colors"
            >
              <X size={18} strokeWidth={1.6} />
            </button>
          </div>
        </div>

        <div
          ref={containerRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          data-testid={`${testIdPrefix}-lightbox-image-container`}
          className={`relative flex-1 min-h-0 rounded-xl bg-[#F7F5EE] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] flex ${
            zoom
              ? "overflow-auto items-start justify-start cursor-grab active:cursor-grabbing"
              : "overflow-hidden items-center justify-center"
          }`}
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={`${index}-${zoom}`}
              src={current.src}
              alt={`${current.label} — ${kicker}`}
              initial={{ opacity: 0, x: zoom ? 0 : 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: zoom ? 0 : -24 }}
              transition={{ duration: 0.25 }}
              onClick={zoom ? undefined : () => setZoom(true)}
              data-testid={`${testIdPrefix}-lightbox-image`}
              className={`block select-none flex-shrink-0 ${
                zoom
                  ? "max-w-none pointer-events-none w-[200%] sm:w-full h-auto"
                  : "max-w-full w-auto h-auto object-contain cursor-zoom-in"
              }`}
              style={zoom ? undefined : { maxHeight: "calc(94vh - 200px)" }}
              draggable={false}
            />
          </AnimatePresence>
          <button
            onClick={onPrev}
            aria-label="Página anterior"
            data-testid={`${testIdPrefix}-lightbox-prev`}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#06231D]/80 border border-white/15 text-white grid place-items-center hover:bg-[#C9A84C] hover:text-[#06231D] hover:border-[#C9A84C] transition-colors backdrop-blur"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={onNext}
            aria-label="Página siguiente"
            data-testid={`${testIdPrefix}-lightbox-next`}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#06231D]/80 border border-white/15 text-white grid place-items-center hover:bg-[#C9A84C] hover:text-[#06231D] hover:border-[#C9A84C] transition-colors backdrop-blur"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 mt-4">
          {pages.map((p, i) => (
            <button
              key={p.src}
              onClick={() => onSelect(i)}
              aria-label={`Ir a la página ${i + 1}`}
              data-testid={`${testIdPrefix}-lightbox-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? "w-7 bg-[#C9A84C]" : "w-1.5 bg-white/25 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
